import { Button, Typography } from '@mui/material';
import {
  MutateWidgetPayload,
  QuerySettingsDto,
  Timespan,
  WidgetDto,
} from '@thingslog/repositories';
import React, { FC, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  createDefaultCustomTimespan,
  createDefaultPredefinedTimespan,
  TimespanPickerFields,
} from '../timespan';
import { TimespanMode, WidgetFieldPath } from './query-settings/models';

interface Props {
  dashboardWidget: WidgetDto;
  disabled?: boolean;
  onSubmit: (payload: MutateWidgetPayload) => void;
  onCancel: () => void;
}

export const WidgetTimeframeForm: FC<Props> = ({
  dashboardWidget,
  disabled = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const { control, handleSubmit, setValue } = useForm<MutateWidgetPayload>({
    mode: 'onChange',
    defaultValues: {
      settings: dashboardWidget.settings,
    },
  });

  const query = useWatch({ control, name: 'settings.query' });
  const supportsTimeframe = !!query && 'timespan' in query;

  const timespanType = useMemo<TimespanMode>(() => {
    if (!query || !('timespan' in query) || !query.timespan) {
      return 'none';
    }

    return query.timespan.type;
  }, [query]);

  const handleTimespanTypeChange = (type: TimespanMode): void => {
    if (!query || !('timespan' in query)) {
      return;
    }

    let timespan: Timespan | null = null;

    if (type === 'predefined') {
      timespan = createDefaultPredefinedTimespan();
    }

    if (type === 'custom') {
      timespan = createDefaultCustomTimespan();
    }

    setValue(
      'settings.query',
      {
        ...query,
        timespan,
      } as QuerySettingsDto,
      { shouldDirty: true, shouldValidate: true }
    );
  };

  if (!supportsTimeframe) {
    return (
      <div className="flex min-h-[180px] flex-col items-center justify-center gap-3 p-4 text-center">
        <Typography variant="subtitle2">
          {t('dashboard_no_widget_timeframe', 'No widget timeframe')}
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          {t(
            'dashboard_no_widget_timeframe_description',
            'This widget query does not support a configurable time period.'
          )}
        </Typography>
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('close_btn', 'Close')}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-4 p-4"
      onSubmit={handleSubmit((payload: MutateWidgetPayload): void => onSubmit(payload))}
    >
      <TimespanPickerFields<MutateWidgetPayload>
        control={control}
        mode={timespanType}
        title={t('dashboard_widget_time_period', 'Widget Time Period')}
        description={t(
          'dashboard_widget_time_period_description',
          'Use the dashboard period or override it only for this widget.'
        )}
        predefinedLabel={t('dashboard_period_preset', 'Preset')}
        predefinedName={'settings.query.timespan.name' as WidgetFieldPath}
        fromDateName={'settings.query.timespan.fromDate' as WidgetFieldPath}
        toDateName={'settings.query.timespan.toDate' as WidgetFieldPath}
        className="flex min-w-0 flex-col gap-3"
        tourTargetBase="query-timespan"
        disabled={disabled}
        modeOptions={[
          { value: 'none', label: t('dashboard_period_dashboard', 'Dashboard') },
          { value: 'predefined', label: t('dashboard_period_preset', 'Preset') },
          { value: 'custom', label: t('dashboard_period_custom', 'Custom') },
        ]}
        onModeChange={handleTimespanTypeChange}
      />

      <div
        className="flex justify-end gap-2 border-0 border-t border-solid border-gray-200 pt-4"
        data-dashboard-tour="widget-form-actions"
      >
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disableElevation disabled={disabled}>
          {t('dashboard_save_period', 'Save Period')}
        </Button>
      </div>
    </form>
  );
};
